import './ModelOverview.scss';
import * as R from 'ramda';
import { useCallback, useMemo, useState, FC } from 'react';
import * as Dfns from 'date-fns/fp';
import { ActualVsPredictedDataInitial } from '@blisspointmedia/bpm-types/dist/MMM';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown } from '@Components/Dropdown';
import { DropdownToggleType } from '@Components/Dropdown/dropdown-constants';
import { InfoTooltip } from '@Components/InfoTooltip';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ChartContainer } from '@Components/ChartContainer';
import { AreaChart } from '@Components/Charts/AreaChart';
import { WidgetContainer } from '@Components/WidgetContainer';
import { Placement, Size } from '@Components/designConstants';
import { dateToMddyy } from '@src/utils/date-utils';
import {
  addFiscalMonths,
  CompanyFiscalCal,
  dateStringToUtcTimestamp,
  getEndOfFiscalMonth,
  getStartOfFiscalQuarter,
  getStartOfFiscalYear,
  toBpmDateString
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { downloadJSONToCSV, downloadPNG, exportToExcel } from '@src/utils/download-utils';
import {
  formatMoney,
  formatNumber,
  formatPercentAsInt
} from '@blisspointmedia/bpm-types/dist/formatUtils';
import { cptbAreaData, currencyFormatter } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { CONVERSION_FORMATTER_TYPE } from '@Pages/MMM/mmm-utils';
import { MdExpandMore, MdDone, MdEdit } from 'react-icons/md';
import { ActualVsPredicted } from './ActualVsPredicted';
import { EditModelWeightingModal } from '../EditModelWeightingModal';
import { ModelConfig, CompositionRow, TransformedSpendData } from '../hroas-types';
import {
  calculateNormalizedWeights as calculateNormalizedWeightsUtil,
  SpendEffectShareCsvRow,
  StackedCSVRow,
  parseMddyyDate,
  formatDateToMddyy,
  isTestRow,
  MEAN_METRIC,
  calculateModelRefreshDate,
  calculateModelMape,
  TTM_WEEKS,
  calculateModelTtmRevenueCredit,
  transformSpendEffectShareCsvToTransformedData,
  getMdbeInducedEffectByDate,
  formatPercentRounded
} from '../utils/hroasUtils';
import {
  buildHistoricalRevenueData,
  calculateOverallTtmRevenueCredit,
  ChartModel,
  HistoricalRevenueDataPoint
} from '../utils/modelOverviewUtils';
import { familySlotIds, Slot } from '../utils/slots';

interface Model {
  id: string;
  label: string;
  appLabel: string;
  enabled: boolean;
  weight: number;
  reasoning?: string;
}

interface ModelOverviewProps {
  company: string;
  disabledPlatform: boolean | undefined;
  groupBy: string;
  kpi: string;
  kpiType: string;
  overallRoasCpa: number | undefined;
  actualVsPredictedData: ActualVsPredictedDataInitial[];
  models: Model[];
  slots: Slot[];
  onModelsChange?: (updatedModels: ModelConfig[]) => void;
  toolbarOutcomeVariable?: string;
  stackedCsvDataPreloaded?: StackedCSVRow[];
  spendEffectShareCsvRows?: SpendEffectShareCsvRow[] | null;
  /** Universal-ROAS rows from RoasCompositionByRevenueStream — Overall row drives Key Results TTM ROAS so values match the modal's Overall bar by construction. */
  compositionDataRoas?: CompositionRow[];
}
export interface ActualVsPredictedData {
  actual_kpi: number;
  date: string;
  predicted_kpi: number;
  train_test: string;
  [key: string]: any;
}

export type DateGroupingKey = 'Day' | 'Week' | 'Month' | 'Quarter' | 'Year';
export interface DateGroupingOption {
  value: DateGroupingKey;
  label: string;
}
export const DATE_GROUPING_OPTIONS: DateGroupingOption[] = [
  { value: 'Week', label: 'Weekly' },
  { value: 'Month', label: 'Monthly' },
  { value: 'Quarter', label: 'Quarterly' }
];

interface RightData {
  kpi: string;
  mean_spend: number;
  refresh_date: string;
  test_end_date: string;
  test_rsquared: number;
  test_size: number;
  train_start_date: string;
  train_size: number;
  test_mape: number;
  test_mae?: number;
  'test_mae '?: number;
}
interface PrettyNameToDataMap {
  dataKey: string;
  name: string;
  value: string | number;
}

interface ModelData {
  name: string;
  firstObservationDate: string;
  lastObservationDate: string;
  trainTestSplit: string;
  mape: number;
  weight: number;
  refreshDate: string;
  roas?: number;
  revenueCreditPercent?: number;
  hasInfoIcon?: boolean;
  enabled?: boolean;
}

export interface SpendCsvRow {
  date: string;
  [key: string]: string | number | undefined;
}

interface DateRange {
  minDate: string | null;
  maxDate: string | null;
}

// Safety floor for both left-column charts (Actual vs Predicted + Historical Spend). Both cards
// `flex: 1`, so they always render at the SAME height and track the Model Overview panel's height
// in BOTH directions — growing when it expands, shrinking when it collapses. This is only the
// readable minimum they won't shrink below; in normal use the panel's content is taller than the
// resulting floor, so the panel drives the height and the charts match it exactly.
const CHART_MIN_HEIGHT = 180;

const ACT_VS_PRED_TITLE = {
  conversion: 'KPI Volume',
  revenue: 'Revenue'
};

const EMPTY_DATE_RANGE = { minDate: null as string | null, maxDate: null as string | null };
const EMPTY_AREA_CHART_DATA: { newD: any[]; areas: any; groupedAreas: any } = {
  newD: [],
  areas: [],
  groupedAreas: []
};

/** Extracts min/max date range from historical revenue data */
const getDateRangeFromHistoricalRevenueData = (
  data: HistoricalRevenueDataPoint[]
): { minDate: string | null; maxDate: string | null } => {
  if (R.isEmpty(data)) {
    return { minDate: null, maxDate: null };
  }
  const dates = data
    .map(item => item.date)
    .filter((date): date is string => Boolean(date))
    .sort();
  if (R.isEmpty(dates)) {
    return { minDate: null, maxDate: null };
  }
  return { minDate: dates[0], maxDate: dates[dates.length - 1] };
};

const testData = {
  meanSpend: 'N/A',
  trainTestSplit: 'N/A',
  testMape: 'N/A',
  overallRoasCpa: 'N/A',
  revenueCreditPercent: 'N/A'
};

const extendMaxDateForGrouping = (
  maxDate: string,
  dateGrouping: DateGroupingKey,
  fiscalCal: CompanyFiscalCal | null
): string => {
  if (!maxDate || !Dfns.isValid(Dfns.parseISO(maxDate))) {
    return maxDate;
  }

  const parsedDate = Dfns.parseISO(maxDate);
  const { startOfWeek } = fiscalCal || {};
  const getStartOfFiscalWeek = Dfns.startOfWeekWithOptions({
    weekStartsOn: R.isNil(startOfWeek) ? 1 : startOfWeek
  });

  switch (dateGrouping) {
    case 'Day':
      return maxDate;
    case 'Week': {
      const weekStart = getStartOfFiscalWeek(parsedDate);
      const weekEnd = Dfns.addDays(6, weekStart);
      return Dfns.format('yyyy-MM-dd', weekEnd);
    }
    case 'Month': {
      const monthEnd = getEndOfFiscalMonth(parsedDate, fiscalCal ?? {});
      return Dfns.format('yyyy-MM-dd', monthEnd);
    }
    case 'Quarter': {
      const quarterStart = getStartOfFiscalQuarter(maxDate, fiscalCal ?? {});
      const quarterEnd = Dfns.subDays(1, addFiscalMonths(3, quarterStart, fiscalCal ?? {}));
      return Dfns.format('yyyy-MM-dd', quarterEnd);
    }
    case 'Year': {
      const yearStart = getStartOfFiscalYear(maxDate, fiscalCal ?? {});
      const yearEnd = Dfns.subDays(1, Dfns.addYears(1, yearStart));
      return Dfns.format('yyyy-MM-dd', yearEnd);
    }
    default:
      return maxDate;
  }
};

const getFilteredSpendDataSource = (
  csvSpendData: TransformedSpendData[],
  dateRange: DateRange,
  dateGrouping?: DateGroupingKey,
  fiscalCal?: CompanyFiscalCal
): TransformedSpendData[] => {
  if (R.isEmpty(csvSpendData)) {
    return [];
  }

  if (dateRange.minDate && dateRange.maxDate) {
    const { minDate } = dateRange;
    let { maxDate } = dateRange;
    if (dateGrouping && dateGrouping !== 'Day' && fiscalCal) {
      maxDate = extendMaxDateForGrouping(maxDate, dateGrouping, fiscalCal);
    }
    return csvSpendData.filter(item => {
      const itemDate = item.date;
      if (!itemDate) {
        return false;
      }
      return itemDate >= minDate && itemDate <= maxDate;
    });
  }

  return csvSpendData;
};

export const ModelOverview: FC<ModelOverviewProps> = ({
  company,
  disabledPlatform,
  groupBy,
  kpi,
  kpiType,
  overallRoasCpa,
  actualVsPredictedData,
  models,
  slots,
  onModelsChange,
  toolbarOutcomeVariable,
  stackedCsvDataPreloaded,
  spendEffectShareCsvRows,
  compositionDataRoas
}) => {
  const [chartDateGrouping, setChartDateGrouping] = useState('Week');
  const [rightData] = useState({} as RightData);
  const [kpiChartYAxisWidth, setKpiChartYAxisWidth] = useState(0);
  const [spendChartYAxisWidth, setSpendChartYAxisWidth] = useState(0);
  const companyEqualsTest = TEST_COMPANIES.includes(company);

  const [trainingDataExpanded, setTrainingDataExpanded] = useState(false);
  const [crossModelStatsExpanded, setCrossModelStatsExpanded] = useState(false);
  const [keyResultsExpanded, setKeyResultsExpanded] = useState(false);
  const stackedCSVData = useMemo(() => stackedCsvDataPreloaded ?? [], [stackedCsvDataPreloaded]);
  const [showEditModal, setShowEditModal] = useState(false);

  const enabledModelIds = useMemo(() => models.filter(m => m.enabled).map(m => m.id), [models]);
  const csvSpendData = useMemo(
    (): TransformedSpendData[] =>
      spendEffectShareCsvRows != null
        ? transformSpendEffectShareCsvToTransformedData(spendEffectShareCsvRows, enabledModelIds)
        : [],
    [spendEffectShareCsvRows, enabledModelIds]
  );

  // Feeds the TTM % Credit to Paid Media fix below (DEV-21662).
  const mdbeInducedEffectByDate = useMemo(
    () => getMdbeInducedEffectByDate(spendEffectShareCsvRows ?? []),
    [spendEffectShareCsvRows]
  );

  const isTrainingRow = useCallback(
    (row: StackedCSVRow): boolean => String(row.is_test).toUpperCase() === 'FALSE',
    []
  );

  const enabledModelsWithWeights = useMemo(
    () =>
      models
        .filter(model => model.enabled)
        .map(model => ({ id: model.id, csvType: model.id, weight: model.weight })),
    [models]
  );
  const rmmmSlotIds = useMemo(() => familySlotIds(slots, 'rmmm'), [slots]);
  const brandEquityIds = useMemo(() => familySlotIds(slots, 'brand_equity'), [slots]);

  const calculateObservationDate = useCallback(
    (modelId: string, mode: 'min' | 'max'): string => {
      if (R.isEmpty(stackedCSVData)) {
        return '';
      }

      const matchingRows = stackedCSVData.filter(row => row.model_type === modelId);

      if (R.isEmpty(matchingRows)) {
        return '';
      }

      const dates = matchingRows
        .map(row => parseMddyyDate(String(row.date)))
        .filter((date): date is Date => date !== null);

      if (R.isEmpty(dates)) {
        return '';
      }

      const targetDate =
        mode === 'min'
          ? new Date(Math.min(...dates.map(d => d.getTime())))
          : new Date(Math.max(...dates.map(d => d.getTime())));

      return formatDateToMddyy(targetDate);
    },
    [stackedCSVData]
  );

  const calculateFirstObservationDate = useCallback(
    (modelId: string): string => calculateObservationDate(modelId, 'min'),
    [calculateObservationDate]
  );

  const calculateLastObservationDate = useCallback(
    (modelId: string): string => calculateObservationDate(modelId, 'max'),
    [calculateObservationDate]
  );

  const calculateCrossModelDate = useMemo(
    () =>
      (mode: 'min' | 'max'): string => {
        if (R.isEmpty(stackedCSVData) || R.isEmpty(enabledModelIds)) {
          return '';
        }

        const matchingRows = stackedCSVData.filter(row => enabledModelIds.includes(row.model_type));

        if (R.isEmpty(matchingRows)) {
          return '';
        }

        const dates = matchingRows
          .map(row => parseMddyyDate(String(row.date)))
          .filter((date): date is Date => date !== null);

        if (R.isEmpty(dates)) {
          return '';
        }

        const targetDate =
          mode === 'min'
            ? new Date(Math.min(...dates.map(d => d.getTime())))
            : new Date(Math.max(...dates.map(d => d.getTime())));

        return formatDateToMddyy(targetDate);
      },
    [stackedCSVData, enabledModelIds]
  );

  const minFirstObservationDateAcrossModels = useMemo(
    () => calculateCrossModelDate('min'),
    [calculateCrossModelDate]
  );

  const maxLastObservationDateAcrossModels = useMemo(
    () => calculateCrossModelDate('max'),
    [calculateCrossModelDate]
  );

  const averageWeeklySpend = useMemo(() => {
    if (R.isEmpty(stackedCSVData)) {
      return null;
    }

    const filteredRows = stackedCSVData.filter(row => {
      const metric = String(row.metric).trim().toLowerCase();
      return rmmmSlotIds.includes(row.model_type) && metric === MEAN_METRIC;
    });

    if (R.isEmpty(filteredRows)) {
      return null;
    }

    const dateSums = new Map<string, number>();

    filteredRows.forEach(row => {
      const date = String(row.date);
      const spend = Number(row.media_spend) || 0;

      if (!dateSums.has(date)) {
        dateSums.set(date, 0);
      }
      dateSums.set(date, (dateSums.get(date) || 0) + spend);
    });

    if (dateSums.size === 0) {
      return null;
    }

    const dailySums = Array.from(dateSums.values());
    const finalAverage = dailySums.reduce((sum, dailySum) => sum + dailySum, 0) / dailySums.length;

    return finalAverage;
  }, [stackedCSVData, rmmmSlotIds]);

  const outcomeVariable = useMemo(() => {
    if (R.isEmpty(stackedCSVData) || R.isEmpty(enabledModelIds)) {
      return null;
    }

    const modelKpiValues: string[] = [];

    stackedCSVData.forEach(row => {
      if (enabledModelIds.includes(row.model_type) && row.model_kpi) {
        modelKpiValues.push(String(row.model_kpi));
      }
    });

    if (R.isEmpty(modelKpiValues)) {
      return null;
    }

    const uniqueKpis = Array.from(new Set(modelKpiValues)).sort();
    return uniqueKpis.join(', ');
  }, [stackedCSVData, enabledModelIds]);

  const trainTestSplit = useMemo(() => {
    if (R.isEmpty(stackedCSVData) || R.isEmpty(enabledModelIds)) {
      return null;
    }

    const normalizedWeights = calculateNormalizedWeightsUtil(enabledModelsWithWeights);

    let weightedTrainingCount = 0;
    enabledModelsWithWeights.forEach(model => {
      const normalizedWeight = normalizedWeights.get(model.csvType) || 0;
      const trainingCount = stackedCSVData.filter(
        row => row.model_type === model.csvType && row.metric === MEAN_METRIC && isTrainingRow(row)
      ).length;
      weightedTrainingCount += normalizedWeight * trainingCount;
    });

    let weightedTotalCount = 0;
    enabledModelsWithWeights.forEach(model => {
      const normalizedWeight = normalizedWeights.get(model.csvType) || 0;
      const totalCount = stackedCSVData.filter(
        row => row.model_type === model.csvType && row.metric === MEAN_METRIC
      ).length;
      weightedTotalCount += normalizedWeight * totalCount;
    });

    if (weightedTotalCount === 0) {
      return null;
    }

    const trainRatio = weightedTrainingCount / weightedTotalCount;
    const testRatio = 1 - trainRatio;

    return {
      train: trainRatio,
      test: testRatio,
      formatted: `${formatPercentAsInt(trainRatio)}/${formatPercentAsInt(testRatio)}`
    };
  }, [stackedCSVData, enabledModelIds, enabledModelsWithWeights, isTrainingRow]);

  const calculateModelTrainTestSplit = useCallback(
    (modelId: string): string => {
      if (R.isEmpty(stackedCSVData)) {
        return 'N/A';
      }

      const trainingCount = stackedCSVData.filter(
        row => row.model_type === modelId && row.metric === MEAN_METRIC && isTrainingRow(row)
      ).length;

      const totalCount = stackedCSVData.filter(
        row => row.model_type === modelId && row.metric === MEAN_METRIC
      ).length;

      if (totalCount === 0) {
        return 'N/A';
      }

      const trainRatio = trainingCount / totalCount;
      const testRatio = 1 - trainRatio;

      return `${formatPercentAsInt(trainRatio)}/${formatPercentAsInt(testRatio)}`;
    },
    [stackedCSVData, isTrainingRow]
  );

  const outOfSampleMape = useMemo(() => {
    if (R.isEmpty(stackedCSVData) || R.isEmpty(enabledModelIds)) {
      return null;
    }

    const normalizedWeights = calculateNormalizedWeightsUtil(enabledModelsWithWeights);

    const weightedApes: number[] = [];

    enabledModelsWithWeights.forEach(model => {
      const normalizedWeight = normalizedWeights.get(model.csvType) || 0;

      const testRows = stackedCSVData.filter(
        row =>
          row.model_type === model.csvType &&
          row.metric === MEAN_METRIC &&
          isTestRow(row) &&
          Number(row.actual) !== 0
      );

      testRows.forEach(row => {
        const actual = Number(row.actual);
        const predicted = Number(row.predicted);

        if (actual !== 0) {
          const ape = Math.abs((actual - predicted) / actual);
          weightedApes.push(normalizedWeight * ape);
        }
      });
    });

    if (R.isEmpty(weightedApes)) {
      return null;
    }

    const averageMape = weightedApes.reduce((sum, ape) => sum + ape, 0) / weightedApes.length;

    return averageMape * 100;
  }, [stackedCSVData, enabledModelIds, enabledModelsWithWeights]);

  const ttmRevenueCreditBySlot = useMemo(
    () =>
      Object.fromEntries(
        models.map(model => [
          model.id,
          model.enabled
            ? calculateModelTtmRevenueCredit(
                model.id,
                stackedCSVData,
                TTM_WEEKS,
                brandEquityIds.includes(model.id) ? mdbeInducedEffectByDate : undefined
              )
            : null
        ])
      ),
    [models, stackedCSVData, brandEquityIds, mdbeInducedEffectByDate]
  );

  const rmmmTtmRevenueCredit = useMemo(
    () =>
      rmmmSlotIds.map(id => ttmRevenueCreditBySlot[id] ?? null).find(value => value !== null) ??
      null,
    [rmmmSlotIds, ttmRevenueCreditBySlot]
  );

  const overallTtmRevenueCredit = useMemo(
    () =>
      calculateOverallTtmRevenueCredit(
        stackedCSVData,
        enabledModelsWithWeights,
        brandEquityIds,
        mdbeInducedEffectByDate
      ),
    [stackedCSVData, enabledModelsWithWeights, brandEquityIds, mdbeInducedEffectByDate]
  );

  // Universal-ROAS Overall row — sourced from the same data that drives the Overall bar of
  // RoasCompositionByRevenueStream. By construction `Σ valuesByModel = rowTotal`, so the
  // per-model and total TTM ROAS values displayed in Key Results add up exactly.
  const universalRoasOverallRow = useMemo(
    () => compositionDataRoas?.find(row => row.label === 'Overall') ?? null,
    [compositionDataRoas]
  );
  const overallTtmRoas = useMemo(
    () => universalRoasOverallRow?.rowTotal ?? null,
    [universalRoasOverallRow]
  );
  const { historicalRevenueData, historicalRevenueDateRange, areaChartDataMemo } = useMemo(() => {
    const empty = {
      historicalRevenueData: [] as HistoricalRevenueDataPoint[],
      historicalRevenueDateRange: EMPTY_DATE_RANGE,
      areaChartDataMemo: EMPTY_AREA_CHART_DATA
    };
    if (R.isEmpty(stackedCSVData) || R.isEmpty(csvSpendData)) {
      return empty;
    }

    const revenueData = buildHistoricalRevenueData(stackedCSVData, enabledModelIds);
    const dateRange = getDateRangeFromHistoricalRevenueData(revenueData);
    const dataSource = getFilteredSpendDataSource(
      csvSpendData,
      dateRange,
      chartDateGrouping as DateGroupingKey
    );
    if (R.isEmpty(dataSource)) {
      return {
        ...empty,
        historicalRevenueData: revenueData,
        historicalRevenueDateRange: dateRange
      };
    }

    // Native filter, not R.propEq: Ramda 0.29 flipped propEq's arg order, so the old
    // `R.propEq('name', 'Covariates')` silently matches nothing (checked `item.Covariates ===
    // 'name'`) and Covariates were never filtered out of the Historical Spend chart.
    const data = dataSource.filter(item => (item as { name?: string }).name !== 'Covariates');
    const firstItem = dataSource[0];
    const data1Key =
      firstItem && 'raw_spend' in firstItem && firstItem.raw_spend !== undefined
        ? 'raw_spend'
        : 'direct_spend';
    return {
      historicalRevenueData: revenueData,
      historicalRevenueDateRange: dateRange,
      areaChartDataMemo: cptbAreaData({
        fiscalCal: {},
        data,
        data1Key,
        groupBy,
        dateGrouping: chartDateGrouping,
        disabledPlatform,
        companyEqualsTest
      })
    };
  }, [
    stackedCSVData,
    csvSpendData,
    enabledModelIds,
    chartDateGrouping,
    companyEqualsTest,
    disabledPlatform,
    groupBy
  ]);

  const mockModelsData: ModelData[] = useMemo(
    () =>
      models.map(model => {
        if (!model.enabled) {
          return {
            name: model.label,
            firstObservationDate: 'off',
            lastObservationDate: 'off',
            trainTestSplit: 'off',
            mape: 0,
            weight: model.weight,
            refreshDate: '1/1/25',
            roas: 0,
            revenueCreditPercent: 0,
            enabled: false
          };
        }
        const firstObservationDate = calculateFirstObservationDate(model.id);
        const lastObservationDate = calculateLastObservationDate(model.id);
        const modelTrainTestSplit = calculateModelTrainTestSplit(model.id);
        const modelMape = calculateModelMape(model.id, stackedCSVData);
        const modelRefreshDate = calculateModelRefreshDate(model.id, stackedCSVData);
        return {
          name: model.label,
          firstObservationDate: firstObservationDate || 'N/A',
          lastObservationDate: lastObservationDate || 'N/A',
          trainTestSplit: modelTrainTestSplit,
          mape: modelMape !== null ? modelMape : 15.4,
          weight: model.weight,
          refreshDate: modelRefreshDate,
          roas: 0.3,
          revenueCreditPercent: 39,
          enabled: true
        };
      }),
    [
      models,
      stackedCSVData,
      calculateFirstObservationDate,
      calculateLastObservationDate,
      calculateModelTrainTestSplit
    ]
  );

  const maxYAxisWidth = useMemo(
    () => Math.max(kpiChartYAxisWidth, spendChartYAxisWidth),
    [kpiChartYAxisWidth, spendChartYAxisWidth]
  );

  const brandEquityEnabled = useMemo(
    () => models.some(m => m.enabled && brandEquityIds.includes(m.id)),
    [models, brandEquityIds]
  );

  const chartModels = useMemo(
    () =>
      models.flatMap((model): ChartModel[] => {
        const family = slots.find(slot => slot.slotId === model.id)?.family;
        return family ? [{ id: model.id, label: model.label, enabled: model.enabled, family }] : [];
      }),
    [models, slots]
  );

  const sharedXAxisDomain = useMemo<[number, number] | undefined>(() => {
    if (!historicalRevenueDateRange.minDate || !historicalRevenueDateRange.maxDate) {
      return undefined;
    }
    return [
      dateStringToUtcTimestamp(toBpmDateString(historicalRevenueDateRange.minDate)),
      dateStringToUtcTimestamp(toBpmDateString(historicalRevenueDateRange.maxDate))
    ];
  }, [historicalRevenueDateRange]);

  // Single source of truth for the x-axis ticks shared by the stacked "Historical"
  // charts (ActualVsPredicted revenue on top, AreaChart spend below). Both charts render
  // a numeric time axis; without an explicit tick set recharts auto-generates ticks per
  // axis, and the two axes have different effective plot widths (spend chart reserves a
  // right margin / the revenue chart may render a second right axis), so the auto-ticks
  // land on different dates. Threading these exact tick timestamps into both charts forces
  // identical tick positions and labels regardless of width.
  const SHARED_X_AXIS_TICK_COUNT = 12;
  const sharedXAxisTicks = useMemo<number[] | undefined>(() => {
    if (!sharedXAxisDomain) {
      return undefined;
    }
    const [start, end] = sharedXAxisDomain;
    if (end <= start) {
      return [start];
    }
    const step = (end - start) / (SHARED_X_AXIS_TICK_COUNT - 1);
    return Array.from({ length: SHARED_X_AXIS_TICK_COUNT }, (_, i) => Math.round(start + step * i));
  }, [sharedXAxisDomain]);

  const spendChartMargin = useMemo(
    () => ({
      top: 20,
      right: brandEquityEnabled ? maxYAxisWidth : 0,
      left: 0,
      bottom: 0
    }),
    [brandEquityEnabled, maxYAxisWidth]
  );

  const actualVsPredictedTooltipFormatter = useCallback(
    (val: number) =>
      val < 1000
        ? CONVERSION_FORMATTER_TYPE[kpiType].format(val, 0)
        : CONVERSION_FORMATTER_TYPE[kpiType].format(val, 1),
    [kpiType]
  );

  const actualVsPredictedYAxisFormatter = useCallback(
    (val: number) => CONVERSION_FORMATTER_TYPE[kpiType].format(val, 0),
    [kpiType]
  );

  const areaChartTooltipFormatter = useCallback(
    (val: number) =>
      val < 1000 ? currencyFormatter.format(val, 0) : currencyFormatter.format(val, 1),
    []
  );

  const areaChartXAxisTickFormatter = useCallback((val: string) => dateToMddyy(val), []);

  const areaChartYAxisTickFormatter = useCallback(
    (val: number) => currencyFormatter.format(val, 0),
    []
  );

  const modelOverviewDataToExport: [string[], (string | number)[]] = useMemo(() => {
    if (!R.isEmpty(rightData) && kpi && overallRoasCpa) {
      const keyToPrettyName: PrettyNameToDataMap[] = [
        {
          dataKey: 'train_start_date',
          name: 'First model observation',
          value: dateToMddyy(rightData.train_start_date)
        },
        {
          dataKey: 'test_end_date',
          name: 'Last model observation',
          value: dateToMddyy(rightData.test_end_date)
        },
        {
          dataKey: 'mean_spend',
          name: 'Average Weekly Spend',
          value: currencyFormatter.format(rightData.mean_spend, 0)
        },
        {
          dataKey: 'kpi',
          name: 'Outcome Variable',
          value: kpi
        },
        {
          dataKey: 'refresh_date',
          name: 'Last refresh date',
          value: dateToMddyy(rightData.refresh_date)
        },
        {
          dataKey: 'N/A',
          name: 'Train/Test split',
          value: `${formatPercentAsInt(rightData.train_size)}/${formatPercentAsInt(
            rightData.test_size
          )}`
        },
        {
          dataKey: 'test_mape',
          name: 'Out-of-sample MAPE',
          value: `${rightData.test_mape.toFixed(1)}%`
        },
        {
          dataKey: 'test_mae',
          name: 'Out-of-sample MAE',
          value: (() => {
            if (rightData['test_mae '] !== undefined) {
              return kpiType === 'revenue'
                ? formatMoney(rightData['test_mae '], 0)
                : formatNumber(rightData['test_mae '], 0);
            }
            if (rightData.test_mae !== undefined) {
              return kpiType === 'revenue'
                ? formatMoney(rightData.test_mae, 0)
                : formatNumber(rightData.test_mae, 0);
            }
            return '';
          })()
        },
        {
          dataKey: 'overallRoasCpa',
          name:
            kpiType === 'revenue' ? 'Trailing Twelve Months ROAS' : 'Trailing Twelve Months CPA',
          value: overallRoasCpa || ''
        }
      ];

      return keyToPrettyName.reduce(
        (acc, cv) => {
          acc[0].push(cv.name);
          acc[1].push(cv.value);
          return acc;
        },
        [[], []] as [string[], (string | number)[]]
      );
    }
    return [[], []];
  }, [rightData, kpi, kpiType, overallRoasCpa]);

  const excelDownloadRight = useCallback(() => {
    exportToExcel(modelOverviewDataToExport, `${company}_model_overview`);
  }, [modelOverviewDataToExport, company]);

  const csvDownloadRight = useCallback(() => {
    downloadJSONToCSV(modelOverviewDataToExport, `${company}_model_overview`);
  }, [modelOverviewDataToExport, company]);

  const excelDownloadActVPred = useCallback(() => {
    exportToExcel(actualVsPredictedData, `${company}_actual_vs_predicted`);
  }, [actualVsPredictedData, company]);

  const pngDownloadActVPred = useCallback(async () => {
    await downloadPNG('.hroas-actual-vs-predicted-graph', `${company}_actual_vs_predicted`);
  }, [company]);

  const excelDownloadAreaGraph = useCallback(() => {
    const dataSource = getFilteredSpendDataSource(
      csvSpendData,
      historicalRevenueDateRange,
      chartDateGrouping as DateGroupingKey
    );

    if (R.isEmpty(dataSource)) {
      return;
    }

    const XLSX_KEYS = [
      'client',
      'kpi',
      'refresh_date',
      'name',
      'date',
      'raw_spend',
      'direct_spend'
    ];

    const availableKeys = XLSX_KEYS.filter(key => dataSource.some(item => item[key] !== undefined));

    const dataToExport = R.map((item: TransformedSpendData) => {
      const picked = R.pick(availableKeys, item);
      const pickedRecord = picked as Record<string, unknown>;
      return {
        ...picked,
        client: (pickedRecord.client as string | undefined) || company,
        kpi: (pickedRecord.kpi as string | undefined) || kpi,
        refresh_date: (pickedRecord.refresh_date as string | undefined) || '',
        direct_spend: (pickedRecord.direct_spend as number | undefined) || item.raw_spend || 0
      };
    }, dataSource);

    exportToExcel(dataToExport, `${company}_area_spend`);
  }, [csvSpendData, company, kpi, historicalRevenueDateRange, chartDateGrouping]);

  const pngDownloadAreaGraph = useCallback(async () => {
    await downloadPNG('.hroas-model-overview .area-chart', `${company}_area`);
  }, [company]);

  const applyWeighting = useCallback(
    (updatedModels: ModelConfig[]) => {
      if (onModelsChange) {
        onModelsChange(updatedModels);
      }
    },
    [onModelsChange]
  );

  const modelsAsConfig: ModelConfig[] = useMemo(
    () =>
      models.map(model => ({
        id: model.id,
        label: model.label,
        appLabel: model.appLabel,
        enabled: model.enabled,
        weight: model.weight,
        reasoning: model.reasoning
      })),
    [models]
  );

  return (
    <WidgetContainer
      collapsible
      header='How well do the models understand our business?'
      subHeader={
        <>
          High out-of-sample prediction accuracy is indicative of reliable causal understanding.
          <InfoTooltip rightLabel='More info' size={Size.SM}>
            Each model is estimated by splitting up the time series data into slices called “train”
            and “test.” The training data is used to estimate the relationships between inputs
            (media investments, contextual variables) and outputs (the KPI). The test data is used
            to evaluate the performance of each trained model “out of sample,” i.e. on observations
            they have not already encountered.
            <br />
            <br /> Out-of-sample predictive accuracy is a critical concept because it proxies what
            we ultimately care about, namely an understanding of likely KPI outputs given a
            combination of novel media inputs. See the out-of-sample statistics at right for
            measures of this concept.
          </InfoTooltip>
        </>
      }
    >
      {!R.isEmpty(stackedCSVData) && (
        <div className='hroas-model-overview'>
          <div className='hroas-left'>
            <ChartContainer
              rightActions={
                <DownloadDropdown
                  size={Size.SM}
                  onClickOptions={[excelDownloadActVPred, pngDownloadActVPred]}
                />
              }
              title={
                <>
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.LG}
                    type={DropdownToggleType.WIDGET_TITLE}
                    className='chart-title-dropdown'
                    value={chartDateGrouping}
                    options={DATE_GROUPING_OPTIONS}
                    onChange={option => setChartDateGrouping(option)}
                  />
                  <div>{`Historical ${ACT_VS_PRED_TITLE[kpiType]}`}</div>
                  <div className='hroas-header-text'>(Actual vs Predicted)</div>
                </>
              }
              minHeight={CHART_MIN_HEIGHT}
            >
              {!R.isEmpty(historicalRevenueData) && (
                <ActualVsPredicted
                  data={historicalRevenueData}
                  dateGrouping={chartDateGrouping}
                  legendValue={ACT_VS_PRED_TITLE[kpiType]}
                  syncId={1}
                  tooltipFormatter={actualVsPredictedTooltipFormatter}
                  yAxisFormatter={actualVsPredictedYAxisFormatter}
                  height='100%'
                  yAxisStaticWidth={maxYAxisWidth}
                  setChartYAxisDynamicWidth={setKpiChartYAxisWidth}
                  models={chartModels}
                  xAxisDomain={sharedXAxisDomain}
                  xAxisTicks={sharedXAxisTicks}
                />
              )}
            </ChartContainer>
            <ChartContainer
              title={
                <>
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.LG}
                    type={DropdownToggleType.WIDGET_TITLE}
                    className='chart-title-dropdown'
                    value={chartDateGrouping}
                    options={DATE_GROUPING_OPTIONS}
                    onChange={option => setChartDateGrouping(option)}
                  />
                  <div>Historical Spend</div>
                </>
              }
              rightActions={
                <DownloadDropdown
                  size={Size.SM}
                  onClickOptions={[excelDownloadAreaGraph, pngDownloadAreaGraph]}
                />
              }
              minHeight={CHART_MIN_HEIGHT}
            >
              {!R.isEmpty(areaChartDataMemo) && (
                <div className='hroas-model-overview-spend'>
                  <AreaChart
                    areas={areaChartDataMemo.areas}
                    data={areaChartDataMemo.newD}
                    dateGrouping={chartDateGrouping}
                    groupedAreas={areaChartDataMemo.groupedAreas}
                    incrementality={false}
                    syncId={1}
                    tooltipFormatter={areaChartTooltipFormatter}
                    xAxisDataKey='date'
                    xAxisTickFormatter={areaChartXAxisTickFormatter}
                    yAxisTickFormatter={areaChartYAxisTickFormatter}
                    yAxisStaticWidth={maxYAxisWidth}
                    setChartYAxisDynamicWidth={setSpendChartYAxisWidth}
                    xAxisDomain={sharedXAxisDomain}
                    xAxisTicks={sharedXAxisTicks}
                    chartMargin={spendChartMargin}
                  />
                </div>
              )}
            </ChartContainer>
          </div>
          <div className='hroas-right'>
            <div className='hroas-model-overview-right-side'>
              <div className='hroas-model-overview-header'>
                <h2 className='hroas-model-overview-title'>Model Overview</h2>
                <div className='hroas-model-overview-actions'>
                  <DownloadDropdown
                    size={Size.SM}
                    onClickOptions={[excelDownloadRight, csvDownloadRight]}
                    menuOptions={['XLSX', 'CSV']}
                  />
                </div>
              </div>

              <div className='hroas-model-overview-sections'>
                {/* Training Data Section */}
                <div className='hroas-model-overview-section'>
                  <InteractiveDiv
                    className='hroas-section-header'
                    onClick={() => setTrainingDataExpanded(!trainingDataExpanded)}
                  >
                    <span className='hroas-section-title'>Training Data</span>
                    <MdExpandMore
                      className={`hroas-expand-icon ${trainingDataExpanded ? 'expanded' : ''}`}
                      size={16}
                    />
                  </InteractiveDiv>
                  <div className='hroas-section-content'>
                    <div className='hroas-data-row-group'>
                      <div className='hroas-data-row'>
                        <span className='hroas-data-label'>First Model Observation Dates:</span>
                        <span className='hroas-data-value'>
                          {minFirstObservationDateAcrossModels || 'N/A'}
                        </span>
                      </div>
                      {trainingDataExpanded && (
                        <div className='hroas-data-subsection'>
                          {mockModelsData.map(model => (
                            <div key={`first-${model.name}`} className='hroas-data-row nested'>
                              <span
                                className={`hroas-data-label secondary ${
                                  model.enabled === false ? 'disabled' : ''
                                }`}
                              >
                                {model.name}:
                              </span>
                              <span
                                className={`hroas-data-value secondary ${
                                  model.enabled === false ? 'disabled' : ''
                                }`}
                              >
                                {model.firstObservationDate}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className='hroas-data-row-group'>
                      <div className='hroas-data-row'>
                        <span className='hroas-data-label'>Last Model Observation Dates:</span>
                        <span className='hroas-data-value'>
                          {maxLastObservationDateAcrossModels || 'N/A'}
                        </span>
                      </div>
                      {trainingDataExpanded && (
                        <div className='hroas-data-subsection'>
                          {mockModelsData.map(model => (
                            <div key={`last-${model.name}`} className='hroas-data-row nested'>
                              <span
                                className={`hroas-data-label secondary ${
                                  model.enabled === false ? 'disabled' : ''
                                }`}
                              >
                                {model.name}:
                              </span>
                              <span
                                className={`hroas-data-value secondary ${
                                  model.enabled === false ? 'disabled' : ''
                                }`}
                              >
                                {model.lastObservationDate}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className='hroas-data-row-group'>
                      <div className='hroas-data-row'>
                        <span className='hroas-data-label'>Average Weekly Spend:</span>
                        <span className='hroas-data-value'>
                          {(() => {
                            if (R.isEmpty(enabledModelIds)) return 'N/A';
                            if (averageWeeklySpend !== null)
                              return currencyFormatter.format(averageWeeklySpend, 0);
                            if (!R.isEmpty(rightData))
                              return currencyFormatter.format(rightData.mean_spend, 0);
                            return testData.meanSpend;
                          })()}
                        </span>
                      </div>
                    </div>

                    <div className='hroas-data-row-group'>
                      <div className='hroas-data-row'>
                        <span className='hroas-data-label'>Outcome Variable:</span>
                        <span className='hroas-data-value'>
                          {(() => {
                            if (R.isEmpty(enabledModelIds)) return 'N/A';
                            if (toolbarOutcomeVariable) return toolbarOutcomeVariable;
                            if (kpiType && ACT_VS_PRED_TITLE[kpiType])
                              return ACT_VS_PRED_TITLE[kpiType];
                            if (outcomeVariable !== null) return outcomeVariable;
                            return kpi.replace(/_/g, ' ');
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='hroas-section-divider' />

                {/* Cross Model Statistics Section */}
                <div className='hroas-model-overview-section'>
                  <InteractiveDiv
                    className='hroas-section-header'
                    onClick={() => setCrossModelStatsExpanded(!crossModelStatsExpanded)}
                  >
                    <span className='hroas-section-title'>Cross Model Statistics</span>
                    <MdExpandMore
                      className={`hroas-expand-icon ${crossModelStatsExpanded ? 'expanded' : ''}`}
                      size={16}
                    />
                  </InteractiveDiv>
                  <div className='hroas-section-content'>
                    <div className='hroas-data-row-group'>
                      <div className='hroas-data-row'>
                        <div className='hroas-data-label-with-icon'>
                          <span className='hroas-data-label'>Train/Test Split:</span>
                          <InfoTooltip size={Size.SM}>
                            This refers to the fraction of data used to fit the models vs the
                            fraction held out for model validation. Setting aside data for model
                            validation is very important in ensuring that the models generalize well
                            to new, unseen data.
                          </InfoTooltip>
                        </div>
                        <span className='hroas-data-value'>
                          {(() => {
                            if (R.isEmpty(enabledModelIds)) return 'N/A';
                            if (trainTestSplit !== null) return trainTestSplit.formatted;
                            if (!R.isEmpty(rightData))
                              return `${formatPercentAsInt(
                                rightData.train_size
                              )}/${formatPercentAsInt(rightData.test_size)}`;
                            return testData.trainTestSplit;
                          })()}
                        </span>
                      </div>
                      {crossModelStatsExpanded && (
                        <div className='hroas-data-subsection'>
                          {mockModelsData.map(model => (
                            <div key={`split-${model.name}`} className='hroas-data-row nested'>
                              <span
                                className={`hroas-data-label secondary ${
                                  model.enabled === false ? 'disabled' : ''
                                }`}
                              >
                                {model.name}:
                              </span>
                              <span
                                className={`hroas-data-value secondary ${
                                  model.enabled === false ? 'disabled' : ''
                                }`}
                              >
                                {model.trainTestSplit}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className='hroas-data-row-group'>
                      <div className='hroas-data-row'>
                        <div className='hroas-data-label-with-icon'>
                          <span className='hroas-data-label'>Out-of-sample MAPE:</span>
                          <InfoTooltip size={Size.SM}>
                            Mean absolute percentage error is a measure of the models&apos;
                            prediction accuracy, reflecting the average magnitude of the difference
                            between actual and predicted values in percentage terms. Its definition
                            is avg(abs((predicted - actual)/actual)).
                          </InfoTooltip>
                        </div>
                        <div className='hroas-data-value-with-icon'>
                          {!R.isEmpty(enabledModelIds) &&
                            (outOfSampleMape !== null
                              ? outOfSampleMape <= 20
                              : !R.isEmpty(rightData) && rightData.test_mape <= 20) && (
                              <MdDone className='hroas-success-check' size={22} />
                            )}
                          <span>
                            {(() => {
                              if (R.isEmpty(enabledModelIds)) return 'N/A';
                              if (outOfSampleMape !== null) return `${outOfSampleMape.toFixed(1)}%`;
                              if (!R.isEmpty(rightData))
                                return `${rightData.test_mape.toFixed(1)}%`;
                              return testData.testMape;
                            })()}
                          </span>
                        </div>
                      </div>
                      {crossModelStatsExpanded && (
                        <div className='hroas-data-subsection'>
                          {mockModelsData.map(model => (
                            <div key={`mape-${model.name}`} className='hroas-data-row nested'>
                              <span
                                className={`hroas-data-label secondary ${
                                  model.enabled === false ? 'disabled' : ''
                                }`}
                              >
                                {model.name}:
                              </span>
                              <span
                                className={`hroas-data-value secondary ${
                                  model.enabled === false ? 'disabled' : ''
                                }`}
                              >
                                {model.enabled === false ? 'off' : `${model.mape.toFixed(1)}%`}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className='hroas-section-divider' />

                <div className='hroas-model-overview-section'>
                  <div className='hroas-section-header'>
                    <span className='hroas-section-title'>Model Weights and Recency</span>
                  </div>
                  <div className='hroas-section-content'>
                    <div className='hroas-weights-header'>
                      <span className='hroas-weights-header-label'>Weight</span>
                      <MdEdit
                        onClick={e => {
                          e.stopPropagation();
                          setShowEditModal(true);
                        }}
                      />
                      <span className='hroas-weights-header-label'>Latest Refresh</span>
                    </div>

                    {models.map(model => {
                      const modelRefreshDate = calculateModelRefreshDate(model.id, stackedCSVData);
                      const isDisabled = !model.enabled;
                      return (
                        <div
                          key={`weight-${model.id}`}
                          className='hroas-data-row hroas-weights-row'
                        >
                          <div className='hroas-model-name-with-badge'>
                            <span className={`hroas-data-label ${isDisabled ? 'disabled' : ''}`}>
                              {model.label}
                            </span>
                          </div>
                          <div className='hroas-weights-values'>
                            <span className={`hroas-data-value ${isDisabled ? 'disabled' : ''}`}>
                              {isDisabled ? 'off' : model.weight.toFixed(2)}
                            </span>
                            <span className={`hroas-data-value ${isDisabled ? 'disabled' : ''}`}>
                              {isDisabled ? 'off' : modelRefreshDate}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className='hroas-section-divider' />

                {/* Key Results Section */}
                <div className='hroas-model-overview-section'>
                  <InteractiveDiv
                    className='hroas-section-header'
                    onClick={() => setKeyResultsExpanded(!keyResultsExpanded)}
                  >
                    <span className='hroas-section-title'>Key Results</span>
                    <MdExpandMore
                      className={`hroas-expand-icon ${keyResultsExpanded ? 'expanded' : ''}`}
                      size={16}
                    />
                  </InteractiveDiv>
                  <div className='hroas-section-content'>
                    <div className='hroas-data-row-group'>
                      <div className='hroas-data-row'>
                        <div className='hroas-data-label-with-icon'>
                          <span className='hroas-data-label'>
                            <OverlayTrigger
                              placement='top'
                              overlay={<Tooltip>Trailing Twelve Months</Tooltip>}
                            >
                              <span>TTM</span>
                            </OverlayTrigger>
                            {' ROAS'}
                          </span>
                          {/*
                            TINUITI LIVE — original explanation for why per-model and multi-model
                            TTM ROAS didn't simply add up. The replacement implementation sources
                            values from RoasCompositionByRevenueStream's Overall row, which by
                            construction uses a universal aligned-spend denominator so per-model
                            values now sum exactly to the total.

                            <InfoTooltip size="sm">
                              <strong>Why don't these simply add up?</strong>
                              <ul className="hroas-tooltip-list">
                                <li>
                                  Multi-model TTM ROAS uses the most recent overlapping
                                  twelve months, whereas each model uses its own most recent twelve
                                  months.
                                </li>
                                <li>
                                  They can use different denominators. Each model uses its own spend
                                  denominator; the multi-model case uses rMMM spend when rMMM is
                                  selected, and an average of Brand Equity and Geo-MMM spend when it
                                  is not.
                                </li>
                              </ul>
                            </InfoTooltip>
                          */}
                          <InfoTooltip size={Size.SM}>
                            <strong>How is this calculated?</strong>
                            <ul className='hroas-tooltip-list'>
                              <li>
                                All values share a universal trailing-twelve-months window and the
                                same aligned-spend denominator (max spend per channel-week across
                                available models), so per-model rows sum exactly to the total.
                              </li>
                              <li>
                                Each per-model row is that model&apos;s contribution to ROAS against
                                the universal denominator &mdash; not the model&apos;s standalone
                                ROAS. Toggling a model off removes its contribution from the total.
                              </li>
                            </ul>
                          </InfoTooltip>
                        </div>
                        <span className='hroas-data-value'>
                          {(() => {
                            if (R.isEmpty(enabledModelIds)) return 'N/A';
                            if (overallTtmRoas !== null) return formatNumber(overallTtmRoas, 2);
                            if (overallRoasCpa) return formatNumber(overallRoasCpa, 2);
                            return testData.overallRoasCpa;
                          })()}
                        </span>
                      </div>
                      {keyResultsExpanded && (
                        <div className='hroas-data-subsection'>
                          {models.map(model => {
                            const modelData = mockModelsData.find(m => m.name === model.label);
                            const modelTtmRoas = universalRoasOverallRow?.valuesByModel[model.id];
                            const displayValue = (() => {
                              if (model.enabled === false) return 'off';
                              if (modelTtmRoas !== undefined) return modelTtmRoas.toFixed(2);
                              return modelData?.roas?.toFixed(2) || '0.00';
                            })();
                            return (
                              <div key={`roas-${model.id}`} className='hroas-data-row nested'>
                                <span
                                  className={`hroas-data-label secondary ${
                                    model.enabled === false ? 'disabled' : ''
                                  }`}
                                >
                                  {model.label}:
                                </span>
                                <span
                                  className={`hroas-data-value secondary ${
                                    model.enabled === false ? 'disabled' : ''
                                  }`}
                                >
                                  {displayValue}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className='hroas-data-row-group'>
                      <div className='hroas-data-row'>
                        <span className='hroas-data-label'>
                          <OverlayTrigger
                            placement='top'
                            overlay={<Tooltip>Trailing Twelve Months</Tooltip>}
                          >
                            <span>TTM</span>
                          </OverlayTrigger>
                          {' % Credit to Paid Media'}
                        </span>
                        <span className='hroas-data-value'>
                          {(() => {
                            if (overallTtmRevenueCredit !== null)
                              return formatPercentRounded(overallTtmRevenueCredit);
                            if (rmmmTtmRevenueCredit !== null)
                              return formatPercentRounded(rmmmTtmRevenueCredit);
                            return testData.revenueCreditPercent;
                          })()}
                        </span>
                      </div>
                      {keyResultsExpanded && (
                        <div className='hroas-data-subsection'>
                          {models.map(model => {
                            const modelData = mockModelsData.find(m => m.name === model.label);
                            const modelCredit = ttmRevenueCreditBySlot[model.id];
                            const displayValue = (() => {
                              if (model.enabled === false) return 'off';
                              if (modelCredit !== null && modelCredit !== undefined)
                                return formatPercentRounded(modelCredit);
                              if (modelData?.revenueCreditPercent !== undefined)
                                return `${modelData.revenueCreditPercent}%`;
                              return '0%';
                            })();
                            return (
                              <div key={`revenue-${model.id}`} className='hroas-data-row nested'>
                                <span
                                  className={`hroas-data-label secondary ${
                                    model.enabled === false ? 'disabled' : ''
                                  }`}
                                >
                                  {model.label}:
                                </span>
                                <span
                                  className={`hroas-data-value secondary ${
                                    model.enabled === false ? 'disabled' : ''
                                  }`}
                                >
                                  {displayValue}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <EditModelWeightingModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        models={modelsAsConfig}
        onSave={applyWeighting}
      />
    </WidgetContainer>
  );
};
